/**
 * Native Dictation Configuration
 *
 * Manages the transcription config sent to Rust for native dictation.
 * Config includes: base URL, auth token, languages, country, and user context
 * for inline post-processing.
 */

import { IS_DICTATION_LOGS_BACKEND_ENABLED } from '@/components/constants/flags';
import { buildOnboardingUserContext } from '@/features/dictation/anonymous-access/buildOnboardingUserContext';
import {
  buildUserContext,
  type UserContext,
} from '@/features/dictation/dictationBubble/store/buildUserContext';
import { ensureCurrentCustomVocabulary } from '@/features/dictation/dictationBubble/store/customVocabularyCache';
import { resolveDictationContext } from '@/features/dictation/dictationBubble/store/getDictationContext';
import {
  getDictationHotkey,
  getUserPlatform,
} from '@/features/dictation/hooks/useDictationHotkey';
import { resolveAnonymousTranscriptionFallbackRegion } from '@/features/dictation/utils/resolveAnonymousTranscriptionFallbackRegion';
import { isDictationLogOptOutEnabled } from '@/features/flags';
import { isCursorAwarenessEnabled } from '@/features/flags/cursor-awareness-flag';
import { isAppsRustAuth0TokenRefreshDesktopEnabled } from '@/features/flags/rust-auth0-token-refresh-desktop';
import { getRegion } from '@/helpers/getRegion';
import {
  getCurrentUser,
  getDefaultDictationInputLanguages,
  getLogDictationsEnabled,
} from '@/hooks/query/useQueryUser';
import { ensureCurrentUserSnippets } from '@/hooks/query/useQueryUserSnippets';
import {
  getTranscriptionBaseUrl,
  getTranscriptionBaseUrlV2,
} from '@/lib/SessionRecordingProvider/t11nClient';
import {
  type DictationAuthMode,
  setDictationConfig,
  setDictationHotkey,
} from '@/tauri/dictation/api';
import { getAuthTokenForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { decodeChord, type DictationTone } from '@heidi/dictate';
import {
  getAppToneMap as getAppToneMapFromCache,
  getDictationFallbackTone as getDictationFallbackToneFromCache,
} from '@heidi/dictate/data';
import { LanguageCode } from '@heidi/schema/ml-scribe';

/** App-identifier → tone map over the app cache; undefined until warmed. */
const getAppToneMap = (): Record<string, DictationTone> | undefined =>
  getAppToneMapFromCache(reactQueryClient);

/** Documentation-category tone over the app cache; undefined until warmed. */
const getDictationFallbackTone = (): DictationTone | undefined =>
  getDictationFallbackToneFromCache(reactQueryClient);

// Track last config state to detect changes
let lastConfigHash = '';

const SUPPORTED_DICTATION_LANGUAGE_CODES = new Set<string>(Object.values(LanguageCode));

// `Intl.Locale` canonicalizes deprecated subtags to modern ones (e.g. Hebrew
// `iw` → `he`), but the transcription enum still uses the deprecated spelling.
// Map the canonical subtag back so those locales aren't lost to the fallback.
const CANONICAL_SUBTAG_ALIASES: Record<string, LanguageCode> = {
  he: LanguageCode.iw,
};

/**
 * OS language as a transcription input for pre-auth dictation, before the user
 * picks one. Falls back to English when the locale is missing/unparseable or
 * its base subtag isn't a `LanguageCode`.
 *
 * ponytail: base subtag only (`de-DE` → `de`), so subtags not in the enum
 * (e.g. Norwegian `nb`, spelled `no`) fall back to English. Upgrade: extend
 * CANONICAL_SUBTAG_ALIASES.
 */
function resolveOsDictationLanguage(): string {
  if (typeof navigator === 'undefined') return LanguageCode.en;
  try {
    const baseSubtag = new Intl.Locale(navigator.language).language.toLowerCase();
    const resolved = CANONICAL_SUBTAG_ALIASES[baseSubtag] ?? baseSubtag;
    return SUPPORTED_DICTATION_LANGUAGE_CODES.has(resolved) ? resolved : LanguageCode.en;
  } catch {
    return LanguageCode.en;
  }
}

/**
 * Build a hash of config values for change detection.
 */
function buildConfigHash(
  transcriptionBaseUrl: string,
  token: string,
  languages: string[],
  country: string | undefined,
  userContext: UserContext | undefined,
  hotkey: string[],
  logDictationsEnabled: boolean | undefined,
  verbalCommandsEnabled: boolean | undefined,
  soundEnabled: boolean | undefined,
  cursorAwarenessEnabled: boolean,
  authMode: DictationAuthMode = 'user_jwt',
  appToneMap: Record<string, DictationTone> | undefined = undefined,
  fallbackTone: DictationTone | undefined = undefined,
  rustAuth0TokenRefresh = false
): string {
  const userContextHash = userContext ? JSON.stringify(userContext) : '';
  const appToneMapHash = appToneMap ? JSON.stringify(appToneMap) : '';
  return `${authMode}|${transcriptionBaseUrl}|${token}|${languages.join(',')}|${country ?? ''}|${userContextHash}|${hotkey.join('+')}|${logDictationsEnabled ?? ''}|${verbalCommandsEnabled ?? ''}|${soundEnabled ?? ''}|${cursorAwarenessEnabled}|${appToneMapHash}|${fallbackTone ?? ''}|${rustAuth0TokenRefresh}`;
}

/**
 * Initialize or update native dictation config.
 * Called on mount and whenever relevant settings change (languages, auth token, etc.).
 *
 * @returns `true` when the JWT config is applied (freshly pushed or already
 * up to date), `false` when it could not be applied yet (no auth token, missing
 * transcription URL, or the IPC failed). Callers that swap the native client
 * away from another mode should only commit that switch on `true`.
 */
export async function updateNativeDictationConfig(): Promise<boolean> {
  const token = await getAuthTokenForHttpClient();
  if (!token) {
    logger.debug({
      event_type: 'DESKTOP_DICTATION_CONFIG_NO_TOKEN',
      details: { reason: 'Waiting for authentication' },
    });
    return false;
  }

  const transcriptionBaseUrl = IS_DICTATION_LOGS_BACKEND_ENABLED()
    ? getTranscriptionBaseUrlV2()
    : getTranscriptionBaseUrl();
  if (!transcriptionBaseUrl) {
    logger.warn({
      event_type: 'DESKTOP_DICTATION_CONFIG_MISSING_URL',
      details: { reason: 'Transcription base URL not configured' },
    });
    return false;
  }

  const inputLanguages = getDefaultDictationInputLanguages() ?? ['en'];

  // Get current user for country header and user context
  const user = getCurrentUser();
  const country = user?.country ?? undefined;

  // Load snippets so the initial config push doesn't forward an empty list.
  await ensureCurrentUserSnippets();
  // Same for the custom vocabulary read by buildUserContext.
  await ensureCurrentCustomVocabulary();
  // Includes verbal-trigger snippets (from cache) for the Rust pass-through.
  // Merge the per-user base context with the dictation context terms inline.
  const baseUserContext = buildUserContext(user);
  const dictationContext = await resolveDictationContext();
  const userContext: UserContext = dictationContext
    ? { ...baseUserContext, dictation_context: dictationContext }
    : baseUserContext;

  const hotkey = getDictationHotkey();

  // Mirror the web `transcribeChunk.ts` gating: only send the preference when
  // platform dictation is on; otherwise `undefined` → Rust receives `None` and
  // omits the field from the multipart POST.
  const logDictationsEnabled = isDictationLogOptOutEnabled()
    ? getLogDictationsEnabled()
    : undefined;

  // Default true matches the backend.
  const verbalCommandsEnabled = user?.dictation?.verbal_commands_enabled ?? true;

  // Server-persisted cue preference (`user.dictation.sound_enabled`). Rust gates
  // the native hotkey/stop cues on this; `true` by default keeps sounds on.
  const soundEnabled = user?.dictation?.sound_enabled ?? true;

  // Release flag is the master kill switch. The per-user preference defaults
  // to true. Rust gates the surrounding-text read on this value.
  const cursorAwarenessEnabled =
    isCursorAwarenessEnabled() && (user?.dictation?.cursor_awareness_enabled ?? true);

  // Per-app tone map + Documentation fallback (`release-dictation-tone-settings` shipped 100%).
  const appToneMap = getAppToneMap();
  const fallbackTone = getDictationFallbackTone();

  const rustAuth0TokenRefresh = isAppsRustAuth0TokenRefreshDesktopEnabled();

  // Check if config has changed
  const newHash = buildConfigHash(
    transcriptionBaseUrl,
    token,
    inputLanguages,
    country,
    userContext,
    hotkey,
    logDictationsEnabled,
    verbalCommandsEnabled,
    soundEnabled,
    cursorAwarenessEnabled,
    'user_jwt',
    appToneMap,
    fallbackTone,
    rustAuth0TokenRefresh
  );
  if (newHash === lastConfigHash) {
    return true;
  }

  try {
    await setDictationConfig(transcriptionBaseUrl, token, inputLanguages, {
      country,
      userContext,
      logDictationsEnabled,
      verbalCommandsEnabled,
      soundEnabled,
      cursorAwarenessEnabled,
      rustAuth0TokenRefresh,
      ...(appToneMap && { appToneMap }),
      ...(fallbackTone && { fallbackTone }),
    });
    const { modifiers, key } = decodeChord(getUserPlatform(), hotkey);
    await setDictationHotkey(modifiers, key);

    // Parse hostname safely for logging (won't affect config success)
    let hostname: string;
    try {
      hostname = new URL(transcriptionBaseUrl).hostname;
    } catch {
      hostname = 'invalid-url';
    }

    logger.info({
      event_type: 'DESKTOP_DICTATION_CONFIG_UPDATED',
      details: {
        region: getRegion(),
        languages: inputLanguages,
        base_url_domain: hostname,
      },
    });

    // Only mark as applied after successful logging
    lastConfigHash = newHash;
    return true;
  } catch (err) {
    logger.error({
      event_type: 'DESKTOP_DICTATION_CONFIG_FAILED',
      details: { error: err },
    });
    return false;
  }
}

/**
 * Test-only: reset the module-global {@link lastConfigHash} so each test starts
 * from a clean dedupe state. Not exported from the package barrel — production
 * code never needs to force a re-apply (a changed config value yields a new hash
 * on its own).
 */
export function _resetNativeDictationConfigForTests(): void {
  lastConfigHash = '';
}

/** Anonymous auth surfaces the pre-auth onboarding flow can target. */
type AnonymousDictationAuthMode = Extract<
  DictationAuthMode,
  'anonymous_onboarding' | 'anonymous_trial'
>;

/**
 * Apply a native dictation config for one of the pre-auth anonymous paths.
 * Shares {@link lastConfigHash} with {@link updateNativeDictationConfig} so mode
 * switches (onboarding ↔ trial ↔ JWT) always re-apply when the effective config
 * differs — `authMode` is part of the hash, so an onboarding→trial swap commits
 * even if every other field is identical.
 *
 * @returns `true` when the config is applied (freshly pushed or already up to
 * date), `false` when it could not be applied yet.
 */
async function updateAnonymousDictationConfig(
  token: string,
  authMode: AnonymousDictationAuthMode
): Promise<boolean> {
  const transcriptionBaseUrl = getTranscriptionBaseUrl(
    resolveAnonymousTranscriptionFallbackRegion()
  );
  if (!transcriptionBaseUrl) {
    logger.warn({
      event_type: 'DESKTOP_DICTATION_CONFIG_MISSING_URL',
      details: { reason: 'Transcription base URL not configured' },
    });
    return false;
  }

  const languages = [resolveOsDictationLanguage()];
  // Keep `input_languages` in sync with `languages[]` — STT vs last-chunk PP read different fields.
  const userContext = { ...buildOnboardingUserContext(), input_languages: languages };

  const newHash = buildConfigHash(
    transcriptionBaseUrl,
    token,
    languages,
    undefined,
    userContext,
    [],
    undefined,
    undefined,
    // Pre-auth onboarding has no user preference yet; omit so cues default on.
    undefined,
    // Pre-auth onboarding never reads cursor context.
    false,
    authMode
  );
  if (newHash === lastConfigHash) {
    return true;
  }

  try {
    await setDictationConfig(transcriptionBaseUrl, token, languages, {
      userContext,
      authMode,
    });
    lastConfigHash = newHash;
    return true;
  } catch (err) {
    logger.error({
      event_type: 'DESKTOP_DICTATION_CONFIG_FAILED',
      details: { error: err },
    });
    return false;
  }
}

/**
 * Configure native dictation for the onboarding steps that precede sign-in
 * (key selection, try-dictation). The supplied token carries the
 * `dictation_context: "onboarding"` claim, so transcribe requests bypass the
 * anonymous quota — these steps are intentionally unlimited.
 */
export async function updateAnonymousOnboardingDictationConfig(
  token: string
): Promise<boolean> {
  return updateAnonymousDictationConfig(token, 'anonymous_onboarding');
}

/**
 * Configure native dictation for the pre-auth trial path (the sign-in step).
 * The supplied token is minted without the onboarding claim, so transcribe
 * requests count against the anonymous quota — the user hits the standard
 * "sign in to keep dictating" limit unless they authenticate.
 */
export async function updateAnonymousTrialDictationConfig(
  token: string
): Promise<boolean> {
  return updateAnonymousDictationConfig(token, 'anonymous_trial');
}
